
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'reference',
  description: 'API reference for endpoints for use with Plaid partners',
  secondLevelToc: true,
  parentTocLevel: 2,
  childTocLevel: 4,
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Processor token endpoints' subtitle={metadata.description} mdxType="Header" />
    <p>{`Processor token endpoints are used to create tokens that are then sent to a Plaid partner for use in a Plaid integration. For a full list of integrations, see the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/integrations"
      }}>{`Plaid Dashboard`}</a>{`. For specific information on Auth integrations, see `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships"
      }}>{`Auth payment partners`}</a>{`. `}</p>
    <Callout mdxType="Callout">
      <p>{`Are you a Plaid processor partner looking for API docs? The documentation on API endpoints for use by partners has moved to `}<a parentName="p" {...{
          "href": "/docs/api/processor-partners"
        }}>{`Processor partner endpoints`}</a>{`.`}</p>
    </Callout>
    <div className="long-endpoint-table">
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`In this section`}</th>
            <th parentName="tr" {...{
              "align": null
            }}></th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/processor/token/create`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Create a processor token`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/processor/stripe/bank_account_token/create`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Create a bank account token for use with Stripe`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/processor/token/permissions/set`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Set product permissions for a processor token`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/processor/token/permissions/get`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Get product permissions for a processor token`}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`See also`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/processor_token/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a test Item and processor token (Sandbox only)`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "processortokencreate"
    }}>{`/processor/token/create`}</h4>
    <Schema method='POST' route='/processor/token/create' type="request" mdxType="Schema">
      <MDXCodeBlock title='/processor/token/create' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`# Exchange the public token from Plaid Link for an access token.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "public_token": "\${PUBLIC_TOKEN}"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/item/public_token/exchange

# Create a processor token for a specific account id.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "\${ACCESS_TOKEN}",
    "account_id": "\${ACCOUNT_ID}",
    "processor": "dwolla"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/processor/token/create
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`import com.plaid.client.model.ItemPublicTokenExchangeRequest;
import com.plaid.client.model.ItemPublicTokenExchangeResponse;
import com.plaid.client.model.ProcessorTokenCreateRequest;
import com.plaid.client.model.ProcessorTokenCreateResponse;

// Change sandbox to production when you're ready to go live!
HashMap<String, String> apiKeys = new HashMap<String, String>();
apiKeys.put("clientId", plaidClientId);
apiKeys.put("secret", plaidSecret);
apiKeys.put("plaidVersion", "2020-09-14");
apiClient = new ApiClient(apiKeys);
apiClient.setPlaidAdapter(ApiClient.Sandbox);

plaidClient = apiClient.createService(PlaidApi.class);

// Exchange the public token from Plaid Link for an access token.
ItemPublicTokenExchangeRequest request = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> exchangeResponse = plaidClient
  .itemPublicTokenExchange(request)
  .execute();

// Create a processor token for a specific account id.
if (exchangeResponse.isSuccessful()) {
  String accessToken = exchangeResponse.body().getAccessToken();
  ProcessorTokenCreateRequest processorRequest = new ProcessorTokenCreateRequest()
    .accessToken(accessToken)
    .processor(ProcessorTokenCreateRequest.ProcessorEnum.DWOLLA)
    .accountId(accountID);

  Response<ProcessorTokenCreateResponse> dwollaResponse = plaidClient
    .processorTokenCreate(processorRequest)
    .execute();

    if (dwollaResponse.isSuccessful()) {
      String dwollaProcessorToken = dwollaResponse.body().getProcessorToken();
    }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`require 'plaid'

# Change sandbox to production when you're ready to go live!
configuration = Plaid::Configuration.new
configuration.server_index = Plaid::Configuration::Environment[ENV['PLAID_ENV'] || 'sandbox']
configuration.api_key['PLAID-CLIENT-ID'] = ENV['PLAID_CLIENT_ID']
configuration.api_key['PLAID-SECRET'] = ENV['PLAID_SECRET']
configuration.api_key['Plaid-Version'] = '2020-09-14'

api_client = Plaid::ApiClient.new(
  configuration
)

client = Plaid::PlaidApi.new(api_client)

# Exchange the public token from Plaid Link for an access token.
item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new(
  {
    public_token: public_token
  }
)
exchange_token_response = client.item_public_token_exchange(item_public_token_exchange_request)
access_token = exchange_token_response.access_token

# Create a processor token for a specific account id.
processor_token_create_request = Plaid::ProcessorTokenCreateRequest.new(
  {
    access_token: access_token,
    account_id: account_id,
    processor: "dwolla"
  }
)
create_response = client.processor_token_create(processor_token_create_request)
processor_token = create_response.processor_token
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`import plaid
from plaid.api import plaid_api
from plaid.model.item_public_token_exchange_request import ItemPublicTokenExchangeRequest
from plaid.model.processor_token_create_request import ProcessorTokenCreateRequest

# Change sandbox to production when you're ready to go live!
configuration = plaid.Configuration(
    host=plaid.Environment.Sandbox,
    api_key={
        'clientId': PLAID_CLIENT_ID,
        'secret': PLAID_SECRET,
        'plaidVersion': '2020-09-14'
    }
)
api_client = plaid.ApiClient(configuration)
client = plaid_api.PlaidApi(api_client)

# Exchange the public token from Plaid Link for an access token.
exchange_request = ItemPublicTokenExchangeRequest(public_token=public_token)
exchange_token_response = client.item_public_token_exchange(exchange_request)
access_token = exchange_token_response['access_token']

# Create a processor token for a specific account id.
create_request = ProcessorTokenCreateRequest(
    access_token=access_token,
    account_id=account_id,
    processor='dwolla'
)
create_response = client.processor_token_create(create_request)
processor_token = create_response['processor_token']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const {
  Configuration,
  PlaidApi,
  PlaidEnvironments,
  ProcessorTokenCreateRequest,
} = require('plaid');
// Change sandbox to production when you're ready to go live!
const configuration = new Configuration({
  basePath: PlaidEnvironments.sandbox,
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': process.env.PLAID_CLIENT_ID,
      'PLAID-SECRET': process.env.PLAID_SECRET,
      'Plaid-Version': '2020-09-14',
    },
  },
});

const plaidClient = new PlaidApi(configuration);

try {
  // Exchange the public_token from Plaid Link for an access token.
  const tokenResponse = await plaidClient.itemPublicTokenExchange({
    public_token: PUBLIC_TOKEN,
  });
  const accessToken = tokenResponse.data.access_token;

  // Create a processor token for a specific account id.
  const request: ProcessorTokenCreateRequest = {
    access_token: accessToken,
    account_id: accountID,
    processor: 'dwolla',
  };
  const processorTokenResponse = await plaidClient.processorTokenCreate(
    request,
  );
  const processorToken = processorTokenResponse.data.processor_token;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v48/plaid"
)

configuration := plaid.NewConfiguration()
configuration.AddDefaultHeader("PLAID-CLIENT-ID", os.Getenv("CLIENT_ID"))
configuration.AddDefaultHeader("PLAID-SECRET", os.Getenv("SECRET"))
configuration.UseEnvironment(plaid.Sandbox)

client := plaid.NewAPIClient(configuration)
ctx := context.Background()

// If not testing in Sandbox, remove these four lines and instead use a publicToken obtained from Link
sandboxInstitution := "ins_109508"
testProducts := []plaid.Products{plaid.PRODUCTS_AUTH}
sandboxPublicTokenResp, _, err := client.PlaidApi.SandboxPublicTokenCreate(ctx).SandboxPublicTokenCreateRequest(
  *plaid.NewSandboxPublicTokenCreateRequest(
    sandboxInstitution,
    testProducts,
  ),
).Execute()
publicToken := sandboxPublicTokenResp.GetPublicToken()

// Exchange the publicToken for an accessToken
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest(publicToken),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()

// Get Accounts
accountsGetResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(
  *plaid.NewAccountsGetRequest(accessToken),
).Execute()
accountID := accountsGetResp.GetAccounts()[0].GetAccountId()

// Create processor token
processorTokenCreateResp, _, err := client.PlaidApi.ProcessorTokenCreate(ctx).ProcessorTokenCreateRequest(
  *plaid.NewProcessorTokenCreateRequest(accessToken, accountID, "dwolla"),
).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v49"
    plaidclient "github.com/plaid/plaid-go/v49/client"
    "github.com/plaid/plaid-go/v49/option"
)

client := plaidclient.NewClient(
    option.WithClientID(plaid.String(os.Getenv("CLIENT_ID"))),
    option.WithSecret(os.Getenv("SECRET")),
    option.WithBaseURL(plaid.Environments.Sandbox),
)
ctx := context.Background()

// If not testing in Sandbox, remove these four lines and instead use a publicToken obtained from Link
sandboxInstitution := "ins_109508"
testProducts := []plaid.Products{plaid.ProductsAuth}
sandboxPublicTokenResp, err := client.Sandbox.CreatePublicToken(ctx, &plaid.SandboxPublicTokenCreateRequest{
    InstitutionID:   sandboxInstitution,
    InitialProducts: testProducts,
})
publicToken := sandboxPublicTokenResp.PublicToken

// Exchange the publicToken for an accessToken
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{
    PublicToken: publicToken,
})
accessToken := exchangePublicTokenResp.AccessToken

// Get Accounts
accountsGetResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{
    AccessToken: accessToken,
})
accountID := accountsGetResp.Accounts[0].AccountID

// Create processor token
processorTokenCreateResp, err := client.Processor.CreateToken(ctx, &plaid.ProcessorTokenCreateRequest{
    AccessToken: accessToken,
    AccountID:   accountID,
    Processor:   "dwolla",
})
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/processor/token/create' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "processortokenpermissionsset"
    }}>{`/processor/token/permissions/set`}</h4>
    <Schema method='POST' route='/processor/token/permissions/set' type="request" mdxType="Schema">
      <MDXCodeBlock title='/processor/token/permissions/set' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "processor_token": "\${PROCESSOR_TOKEN}",
    "products": ["auth", "balance", "identity"]
  }' \\
  -X POST \\
  https://sandbox.plaid.com/processor/token/permissions/set
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`  ProcessorTokenPermissionsSetRequest request = new ProcessorTokenPermissionsSetRequest()
    .processorToken(processorToken)
    .products(products); // List<Products>

  Response<ProcessorTokenPermissionsSetResponse> response = client()
    .processorTokenPermissionsSet(request)
    .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`processor_token_permissions_set_request = Plaid::ProcessorTokenPermissionsSetRequest.new(
  {
    processor_token: processor_token,
    products: products
  }
)
response = client.processor_token_permissions_set(processor_token_permissions_set_request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = ProcessorTokenPermissionsSetRequest(
    processor_token=processor_token,
    products=products # [Products]
)
response = client.processor_token_permissions_set(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`try {
  const request: ProcessorTokenPermissionsSetRequest = {
    processor_token: processorToken,
    products: ['auth', 'balance', 'identity'],
  };
  const response = await plaidClient.processorTokenPermissionsSet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`processorTokenPermissionsSetResp, _, err := client.PlaidApi.ProcessorTokenPermissionsSet(ctx).ProcessorTokenPermissionsSetRequest(
  *plaid.NewProcessorTokenPermissionsSetRequest(processorToken, products),
).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`processorTokenPermissionsSetResp, err := client.Processor.SetTokenPermissions(ctx, &plaid.ProcessorTokenPermissionsSetRequest{
    ProcessorToken: processorToken,
    Products:       products,
})
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/processor/token/permissions/set' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "processortokenpermissionsget"
    }}>{`/processor/token/permissions/get`}</h4>
    <Schema method='POST' route='/processor/token/permissions/get' type="request" mdxType="Schema">
      <MDXCodeBlock title='/processor/token/permissions/get' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "processor_token": "\${PROCESSOR_TOKEN}"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/processor/token/permissions/get
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`  ProcessorTokenPermissionsGetRequest request = new ProcessorTokenPermissionsGetRequest()
    .processorToken(processorToken);

  Response<ProcessorTokenPermissionsGetResponse> response = client()
    .processorTokenPermissionsGet(request)
    .execute();

  List<Products> products = response.body().getProducts();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`processor_token_permissions_get_request = Plaid::ProcessorTokenPermissionsGetRequest.new(
  {
    processor_token: processor_token,
  }
)
response = client.processor_token_permissions_get(processor_token_permissions_get_request)
products = response.products
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = ProcessorTokenPermissionsGetRequest(
    processor_token=processor_token,
)
response = client.processor_token_permissions_get(request)
products = response['products']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`try {
  const request: ProcessorTokenPermissionsGetRequest = {
    processor_token: processorToken,
  };
  const response = await plaidClient.processorTokenPermissionsGet(request);
  const products = response.data.products;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`processorTokenPermissionsGetResp, _, err := client.PlaidApi.ProcessorTokenPermissionsGet(ctx).ProcessorTokenPermissionsGetRequest(
  *plaid.NewProcessorTokenPermissionsGetRequest(processorToken),
).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`processorTokenPermissionsGetResp, err := client.Processor.GetTokenPermissions(ctx, &plaid.ProcessorTokenPermissionsGetRequest{ProcessorToken: processorToken})
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/processor/token/permissions/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "processorstripebank_account_tokencreate"
    }}>{`/processor/stripe/bank_account_token/create`}</h4>
    <Schema method='POST' route='/processor/stripe/bank_account_token/create' type="request" mdxType="Schema">
      <MDXCodeBlock title='/processor/stripe/bank_account_token/create' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`// Change sandbox to production when you're ready to go live!
const {
  Configuration,
  PlaidApi,
  PlaidEnvironments,
  ProcessorStripeBankAccountTokenCreateRequest,
} = require('plaid');
const configuration = new Configuration({
  basePath: PlaidEnvironments[process.env.PLAID_ENV],
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': process.env.PLAID_CLIENT_ID,
      'PLAID-SECRET': process.env.PLAID_SECRET,
      'Plaid-Version': '2020-09-14',
    },
  },
});

const plaidClient = new PlaidApi(configuration);

try {
  // Exchange the public_token from Plaid Link for an access token.
  const tokenResponse = await plaidClient.itemPublicTokenExchange({
    public_token: PUBLIC_TOKEN,
  });
  const accessToken = tokenResponse.data.access_token;

  // Generate a bank account token
  const request: ProcessorStripeBankAccountTokenCreateRequest = {
    access_token: accessToken,
    account_id: accountID,
  };
  const stripeTokenResponse = await plaidClient.processorStripeBankAccountTokenCreate(
    request,
  );
  const bankAccountToken = stripeTokenResponse.data.stripe_bank_account_token;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`# Exchange token
curl -X POST https://sandbox.plaid.com/item/public_token/exchange \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "public_token": "\${PUBLIC_TOKEN}"
}'

# Create bank account token
curl -X POST https://sandbox.plaid.com/processor/stripe/bank_account_token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": "\${ACCESS_TOKEN}",
  "account_id": "\${ACCOUNT_ID}"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`require 'plaid'

# Change sandbox to production when you're ready to go live!
configuration = Plaid::Configuration.new
configuration.server_index = Plaid::Configuration::Environment[ENV['PLAID_ENV'] || 'sandbox']
configuration.api_key['PLAID-CLIENT-ID'] = ENV['PLAID_CLIENT_ID']
configuration.api_key['PLAID-SECRET'] = ENV['PLAID_SECRET']
configuration.api_key['Plaid-Version'] = '2020-09-14'

api_client = Plaid::ApiClient.new(
  configuration
)

client = Plaid::PlaidApi.new(api_client)

item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new(
  {
    public_token: public_token
  }
)
exchange_token_response = client.item_public_token_exchange(item_public_token_exchange_request)
access_token = exchange_token_response.access_token

processor_token_create_request = Plaid::ProcessorStripeBankAccountTokenCreateRequest.new(
  {
    access_token: access_token,
    account_id: account_id
  }
)
stripe_response = client.processor_stripe_bank_account_token_create(processor_token_create_request)
bank_account_token = stripe_response.stripe_bank_account_token
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`import com.plaid.client.model.ItemPublicTokenExchangeRequest;
import com.plaid.client.model.ItemPublicTokenExchangeResponse;
import com.plaid.client.model.ProcessorStripeBankAccountTokenCreateRequest;
import com.plaid.client.model.ProcessorStripeBankAccountTokenCreateResponse;

// Change sandbox to production when you're ready to go live!
HashMap<String, String> apiKeys = new HashMap<String, String>();
apiKeys.put("clientId", plaidClientId);
apiKeys.put("secret", plaidSecret);
apiKeys.put("plaidVersion", "2020-09-14");
apiClient = new ApiClient(apiKeys);
apiClient.setPlaidAdapter(ApiClient.Sandbox);

plaidClient = apiClient.createService(PlaidApi.class);

// Exchange the public token from Plaid Link for an access token.
ItemPublicTokenExchangeRequest request = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> exchangeResponse = plaidClient
  .itemPublicTokenExchange(request)
  .execute();

if (exchangeResponse.isSuccessful()) {
  String accessToken = exchangeResponse.body().getAccessToken();

  ProcessorStripeBankAccountTokenCreateRequest stripeRequest = new ProcessorStripeBankAccountTokenCreateRequest()
    .accessToken(accessToken)
    .accountId("FooBarAccountId");

  Response<ProcessorStripeBankAccountTokenCreateResponse> stripeResponse = plaidClient
    .processorStripeBankAccountTokenCreate(stripeRequest)
    .execute();

  if (stripeResponse.isSuccessful()) {
    String bankAccountToken = stripeResponse.body().getStripeBankAccountToken();
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`import plaid
from plaid.api import plaid_api
from plaid.model.item_public_token_exchange_request import ItemPublicTokenExchangeRequest
from plaid.model.processor_stripe_bank_account_token_create_request import ProcessorStripeBankAccountTokenCreateRequest

# Change sandbox to production when you're ready to go live!
configuration = plaid.Configuration(
    host=plaid.Environment.Sandbox,
    api_key={
        'clientId': PLAID_CLIENT_ID,
        'secret': PLAID_SECRET,
        'plaidVersion': '2020-09-14'
    }
)
api_client = plaid.ApiClient(configuration)
client = plaid_api.PlaidApi(api_client)

exchange_request = ItemPublicTokenExchangeRequest(public_token=public_token)
exchange_token_response = client.item_public_token_exchange(exchange_request)
access_token = exchange_token_response['access_token']

request = ProcessorStripeBankAccountTokenCreateRequest(
    access_token=access_token,
    account_id=account_id,
)
stripe_response = client.processor_stripe_bank_account_token_create(request)
bank_account_token = stripe_response['stripe_bank_account_token']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v48/plaid"
)

configuration := plaid.NewConfiguration()
configuration.AddDefaultHeader("PLAID-CLIENT-ID", os.Getenv("CLIENT_ID"))
configuration.AddDefaultHeader("PLAID-SECRET", os.Getenv("SECRET"))
configuration.UseEnvironment(plaid.Sandbox)

client := plaid.NewAPIClient(configuration)
ctx := context.Background()

// If not testing in Sandbox, remove these four lines and instead use a publicToken obtained from Link
sandboxInstitution := "ins_109508"
testProducts := []plaid.Products{plaid.PRODUCTS_AUTH}
sandboxPublicTokenResp, _, err := client.PlaidApi.SandboxPublicTokenCreate(ctx).SandboxPublicTokenCreateRequest(
  *plaid.NewSandboxPublicTokenCreateRequest(
    sandboxInstitution,
    testProducts,
  ),
).Execute()
publicToken := sandboxPublicTokenResp.GetPublicToken()

// Exchange the publicToken for an accessToken
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest(publicToken),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()

// Get Accounts
accountsGetResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(
  *plaid.NewAccountsGetRequest(accessToken),
).Execute()
accountID := accountsGetResp.GetAccounts()[0].GetAccountId()

// Create Stripe token
stripeTokenCreateResp, _, err := client.PlaidApi.ProcessorStripeBankAccountTokenCreate(ctx).ProcessorStripeBankAccountTokenCreateRequest(
  *plaid.NewProcessorStripeBankAccountTokenCreateRequest(accessToken, accountID),
).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v49"
    plaidclient "github.com/plaid/plaid-go/v49/client"
    "github.com/plaid/plaid-go/v49/option"
)

client := plaidclient.NewClient(
    option.WithClientID(plaid.String(os.Getenv("CLIENT_ID"))),
    option.WithSecret(os.Getenv("SECRET")),
    option.WithBaseURL(plaid.Environments.Sandbox),
)
ctx := context.Background()

// If not testing in Sandbox, remove these four lines and instead use a publicToken obtained from Link
sandboxInstitution := "ins_109508"
testProducts := []plaid.Products{plaid.ProductsAuth}
sandboxPublicTokenResp, err := client.Sandbox.CreatePublicToken(ctx, &plaid.SandboxPublicTokenCreateRequest{
    InstitutionID:   sandboxInstitution,
    InitialProducts: testProducts,
})
publicToken := sandboxPublicTokenResp.PublicToken

// Exchange the publicToken for an accessToken
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{
    PublicToken: publicToken,
})
accessToken := exchangePublicTokenResp.AccessToken

// Get Accounts
accountsGetResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{
    AccessToken: accessToken,
})
accountID := accountsGetResp.Accounts[0].AccountID

// Create Stripe token
stripeTokenCreateResp, err := client.Processor.CreateStripeBankAccountToken(ctx, &plaid.ProcessorStripeBankAccountTokenCreateRequest{
    AccessToken: accessToken,
    AccountID:   accountID,
})
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/processor/stripe/bank_account_token/create' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "processortokencreate",
        level: 4,
        title: "/processor/token/create",
      },
{
        id: "processortokenpermissionsset",
        level: 4,
        title: "/processor/token/permissions/set",
      },
{
        id: "processortokenpermissionsget",
        level: 4,
        title: "/processor/token/permissions/get",
      },
{
        id: "processorstripebank_account_tokencreate",
        level: 4,
        title: "/processor/stripe/bank_account_token/create",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
